import { ELEMENT_COLORS } from "../assets/elements.js";
import { GALAXY_MAP_DATA } from "../assets/galaxy_map_tiles.js";

export class GalaxyView {
  constructor(state) {
    this.container = document.getElementById('galaxy-grid');
    if (!this.container) {
      throw new Error(`Container element with ID '${containerId}' not found.`);
    }

    this.gameState = state;

    

    // Map & Spatial Data
    this.tiles = [];
    this.tileMap = new Map();

    // Camera Focal Point (Grid World Coords)
    this.centerX = 0;
    this.centerY = 0;

    // Zoom Bounds (Count of visible tiles across width/height)
    this.minMapSize = 3;
    this.maxMapSize = 15;
    this.baseMapSize = 5;
    this.currentMapSize = this.baseMapSize;

    this.initialWorlds = {
      "fire": { xPos: -1, yPos: 18 },
      "earth": { xPos: 13, yPos: 14 },
      "light": { xPos: 18, yPos: 2 },
      "wind": { xPos: 16, yPos: -6 },
      "cold": { xPos: 5, yPos: -17 },
      "water": { xPos: -8, yPos: -15 },
      "lightning": { xPos: -15, yPos: -7 },
      "dark": { xPos: -14, yPos: 11 },
    }

    // this.initialWorlds.forEach(world => {
    //   this.gameState.worldHandler.setupDefaultWorld(this.getTile(world.xPos, world.yPos));

    // })

    this.currentElement = "fire";

    this.currentWorldDisplay = document.querySelector("#galaxy-current-world");

    this.init(GALAXY_MAP_DATA);
  }

  init(mapData) {
    this.setupEventListeners();
    if (mapData) {
      this.loadMapData(mapData);
    }
  }

  loadMapData(data) {
    this.tiles = data.tiles || [];
    this.tileMap.clear();
    this.tiles.forEach(t => this.tileMap.set(t.seed, t));
    let currentTile = this.tileMap.get(this.gameState.worldHandler.currentWorldId);
    if (currentTile) {
      this.centerX = currentTile.x;
      this.centerY = currentTile.y;
    } else {
      let positionData = this.initialWorlds[this.currentElement]
      this.centerX = positionData.xPos;
      this.centerY = positionData.yPos;
    }
    

    this.drawGrid();
    this.updateCurrentWorldDisplay();
  }

  /**
   * Adjusts the visible grid size (zoom) by odd increments (±2)
   */
  updateMapSize(amount) {
    let newMapSize = this.currentMapSize + amount;
    if (newMapSize <= this.maxMapSize && newMapSize >= this.minMapSize) {
      this.currentMapSize = newMapSize;
      this.drawGrid();
    }
  }

  /**
   * Moves center focal coordinate to (x, y)
   */
  moveCenter(x, y) {
    this.centerX = x;
    this.centerY = y;
    this.drawGrid();
    this.updateCurrentWorldDisplay();
  }

  getTile(x, y) {
    return this.tileMap.get(`${x}-${y}`) || null;
  }

  getTileColor(elements) {
    if (!elements || elements.length === 0) return '#666666';
    const key = elements.join('-');
    return ELEMENT_COLORS[key] || ELEMENT_COLORS["all"];
  }

  /**
   * Renders single or multi-element icon compositions.
   * Leverages pre-compiled sprite sheet icons (`#icon-a-b-c-d`) or dynamically
   * lays out 4 elements in a Diamond or 2x2 Square formation.
   */
  renderTileIcon(elements) {
    if (!elements || elements.length === 0) {
      return `<svg class="tile-icon" viewBox="0 0 400 400"><use href="#icon-default"></use></svg>`;
    }

    const elemArray = Array.isArray(elements) ? elements : [elements];
    const comboKey = elemArray.join('-').toLowerCase();

    // 1. Check if a pre-combined SVG symbol exists in the stylesheet sprite sheet
    // (e.g. #icon-light-cold-water-lightning or #icon-fire-earth)
    const combinedSymbol = document.getElementById(`icon-${comboKey}`);
    if (combinedSymbol) {
      return `
        <svg class="tile-icon" viewBox="0 0 400 400">
          <use href="#icon-${comboKey}"></use>
        </svg>
      `;
    }

  }

  setupEventListeners() {
    // Mouse Wheel Zooming
    this.container.addEventListener('wheel', (e) => {
      e.preventDefault();
      if (e.deltaY < 0) {
        this.updateMapSize(-2); // Zoom in
      } else {
        this.updateMapSize(2);  // Zoom out
      }
    }, { passive: false });

    // Delegated Click Handling (Matches navigationMenu pattern)
    this.container.addEventListener('click', (e) => {
      if (this.isDragging) return;

      const tileEl = e.target.closest('.galaxy-tile');
      if (!tileEl) return;

      const x = parseInt(tileEl.dataset.x, 10);
      const y = parseInt(tileEl.dataset.y, 10);

      const isCenter = (x === this.centerX && y === this.centerY);
      const tile = this.getTile(x, y);

      
      if (typeof this.onTileClick === 'function') {
        this.onTileClick(tile);
      }
    
      this.moveCenter(x, y);
      
      if (isCenter) {
        this.gameState.uiInfo.swapPages = true;
      }
    });
  }

  onTileClick(tile) {
    this.gameState.swapWorld(tile);
  }

  formatElementName(elements) {
    let elementStr = "none";

    if (elements.length == 1) {
      elementStr = elements[0]
    } else if (elements.length == 2) {
      elementStr = elements.join('-').toLowerCase();
    } else if (elements.length == 4) {
      if (elements[0] == "fire") {
        elementStr = "square"
      } else {
        elementStr = "diamond"
      }
    } else {
      elementStr = "all"
    }

    return elementStr;
  }

  updateCurrentWorldDisplay() {
    const tile = this.getTile(this.centerX, this.centerY);
    const elementName = this.formatElementName(tile.elements)
    this.currentWorldDisplay.innerText = `(${tile.x}, ${tile.y}) Tier ${tile.tier} ${elementName} world`;
  }

  drawGrid() {
    const gridSize = this.currentMapSize;
    const offset = Math.floor(gridSize / 2);
    const totalCells = gridSize * gridSize;

    if (this.container.children.length !== totalCells) {
      this.container.innerHTML = '';
      this.container.style.setProperty('--grid-rows', gridSize);
      this.container.style.setProperty('--grid-cols', gridSize);
    }

    let cellIndex = 0;

    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const worldX = this.centerX + (col - offset);
        const worldY = this.centerY - (row - offset);
        const isCenter = (col === offset && row === offset);

        const tile = this.getTile(worldX, worldY);
        let cellEl = this.container.children[cellIndex] || null;

        if (!cellEl) {
          cellEl = document.createElement('div');
          this.container.appendChild(cellEl);
        }

        cellEl.dataset.x = worldX;
        cellEl.dataset.y = worldY;

        if (tile) {
          const mainColor = this.getTileColor(tile.elements);
          const iconHTML = this.renderTileIcon(tile.elements);

          cellEl.className = `galaxy-tile ${isCenter ? 'is-center' : ''} grid-item${isCenter ? '-center' : ''}`;
          cellEl.style.backgroundColor = '#222';
          cellEl.style.setProperty('--element-color', mainColor);

          cellEl.innerHTML = `
            <div class="tile-row top-row">
              <span class="tile-coords">(${worldX}, ${worldY})</span>
              ${iconHTML}
            </div>
            <div class="tile-row bottom-row">
              <span class="tile-stat-left">T${tile.tier || 1}</span>
              <span class="tile-stat-right">${tile.seed ? tile.seed.toString() : ''}</span>
            </div>
          `;
        } else {
          // Empty Slot: Dim border and no interactions
          cellEl.className = `galaxy-tile is-empty`;
          cellEl.style.backgroundColor = '';
          cellEl.style.removeProperty('--element-color');
          cellEl.innerHTML = '';
        }

        cellIndex++;
      }
    }
  }
}
